import { isDefined } from "@clipboard-health/util-ts";
import {
  type ExtraWorkedTimeRequestEntity,
  ExtraWorkedTimeRequestStatus,
  ExtraWorkedTimeRequestStatusUpdater,
} from "@src/appV2/Shifts/MandatoryBreakPolicy/api/useExtraWorkedTimeRequests";
import { useGetShiftState } from "@src/appV2/Shifts/Shift/ShiftState/api/useGetShiftState";
import { type WorkerShift } from "@src/appV2/Shifts/Shift/types";
import { differenceInDays, parseISO } from "date-fns";
import { useMemo } from "react";

import { useGetWorkerShift } from "../../api/useGetWorkerShift";
import { useDismissedExtraWorkedTimeRequests } from "../../ShiftHistory/hooks/useDismissedExtraWorkedTimeRequests";
import { type MissedPunchRequestStatus } from "../types";
import { useGetExtraWorkedTimeRequestsByShift } from "./useGetExtraWorkedTimeRequestsByShift";

const TIMESHEET_SUBMISSION_WINDOW_IN_DAYS = 30;

interface PastShiftDetailsData {
  shift: WorkerShift;
  missedPunchRequestStatus: MissedPunchRequestStatus;
  isFullShiftPayment: boolean;
  isMissedPunchRequestEnabled: boolean;
  canSubmitTimesheet: boolean;
  requestedAmountInDollars?: number;
  totalPayInDollars: number;
  hourlyRateInDollars: number;
  requestExpiryTime?: string;
  requestId?: string;
}

interface PastShiftDetailsParams {
  shiftId: string;
}

interface PastShiftDetailsResult {
  data: PastShiftDetailsData | undefined;
  isLoading: boolean;
  isError: boolean;
  dismissRequest: (requestId: string) => void;
}

export function useGetPastShiftDetailsData(params: PastShiftDetailsParams): PastShiftDetailsResult {
  const { shiftId } = params;

  const isEnabled = isDefined(shiftId) && shiftId.length > 0;

  const {
    data: shiftData,
    isLoading: isShiftLoading,
    isError: isShiftError,
  } = useGetWorkerShift(shiftId, {
    enabled: isEnabled,
  });

  const {
    data: shiftState,
    isLoading: isShiftStateLoading,
    isError: isShiftStateError,
  } = useGetShiftState(
    { shiftStateDependencies: { _id: shiftId } },
    {
      enabled: isEnabled,
    }
  );

  const isMissedPunchRequestEnabled = shiftState?.metadata.isMissedPunchRequestEnabled ?? false;

  const {
    data: extraWorkedTimeRequestsData,
    isLoading: isExtraWorkedTimeRequestsLoading,
    isError: isExtraWorkedTimeRequestsError,
  } = useGetExtraWorkedTimeRequestsByShift(shiftId, {
    enabled: isEnabled && isMissedPunchRequestEnabled,
  });

  const requestIds = useMemo(
    () => extraWorkedTimeRequestsData?.data.map(({ id }) => id),
    [extraWorkedTimeRequestsData]
  );

  const { isRequestDismissed, dismissRequest } = useDismissedExtraWorkedTimeRequests(requestIds);

  const shift = shiftData?.response;

  const pastShiftDetailsData = useMemo((): PastShiftDetailsData | undefined => {
    if (!isDefined(shift)) {
      return undefined;
    }

    const relevantRequest = getLatestActionableRequest(
      extraWorkedTimeRequestsData?.data ?? [],
      isRequestDismissed
    );

    const hourlyRateInDollars = shift.offer?.pay.value ?? shift.finalPay ?? 0;
    const totalPayInDollars = shift.originalAmount ?? 0;

    return {
      shift,
      missedPunchRequestStatus: getMissedPunchRequestStatus(
        relevantRequest?.attributes.status,
        relevantRequest?.attributes.statusUpdatedBy
      ),
      isFullShiftPayment: relevantRequest?.attributes.isFullShiftPayment ?? false,
      isMissedPunchRequestEnabled,
      canSubmitTimesheet:
        differenceInDays(new Date(), parseISO(shift.start)) <= TIMESHEET_SUBMISSION_WINDOW_IN_DAYS,
      requestedAmountInDollars: isDefined(relevantRequest)
        ? ((relevantRequest.attributes.timeRequestedInMinutes ?? 0) / 60) * hourlyRateInDollars
        : undefined,
      totalPayInDollars,
      hourlyRateInDollars,
      requestExpiryTime: relevantRequest?.meta.computedExpirationTime,
      requestId: relevantRequest?.id,
    };
  }, [shift, extraWorkedTimeRequestsData, isMissedPunchRequestEnabled, isRequestDismissed]);

  return {
    data: pastShiftDetailsData,
    isLoading:
      isShiftLoading ||
      isShiftStateLoading ||
      (isMissedPunchRequestEnabled && isExtraWorkedTimeRequestsLoading),
    isError:
      isShiftError ||
      isShiftStateError ||
      (isMissedPunchRequestEnabled && isExtraWorkedTimeRequestsError),
    dismissRequest,
  };
}

function getMissedPunchRequestStatus(
  status?: ExtraWorkedTimeRequestStatus,
  statusUpdatedBy?: ExtraWorkedTimeRequestStatusUpdater
): MissedPunchRequestStatus {
  if (!isDefined(status)) {
    return "none";
  }

  if (status === ExtraWorkedTimeRequestStatus.DECLINED) {
    if (statusUpdatedBy === ExtraWorkedTimeRequestStatusUpdater.SYSTEM) {
      return "expired";
    }

    return "declined";
  }

  if (status === ExtraWorkedTimeRequestStatus.PENDING) {
    return "pending";
  }

  return "none";
}

/**
 * Returns the most recent request by createdAt, or undefined if there are
 * no requests, the latest request has been approved (i.e., fully resolved),
 * or the latest request has been dismissed by the user.
 */
function getLatestActionableRequest(
  requests: ExtraWorkedTimeRequestEntity[],
  isRequestDismissed: (requestId: string) => boolean
): ExtraWorkedTimeRequestEntity | undefined {
  const [latest] = requests.toSorted(
    (a, b) =>
      parseISO(b.attributes.createdAt).getTime() - parseISO(a.attributes.createdAt).getTime()
  );

  if (!isDefined(latest)) {
    return undefined;
  }

  if (
    latest.attributes.status === ExtraWorkedTimeRequestStatus.APPROVED ||
    isRequestDismissed(latest.id)
  ) {
    return undefined;
  }

  return latest;
}
